/*
 * Copyright 2023 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { TableColumn } from '@backstage/core-components';
import { columnFactories } from './columns';
import { CatalogTableColumnsFunc, CatalogTableRow } from './types';

// The defaultCatalogTableColumnsFunc symbol is not directly exported, but through the
// CatalogTable.defaultColumnsFunc field.
/** @public */
export const defaultCatalogTableColumnsFunc: CatalogTableColumnsFunc = ({
  filters,
  entities,
}) => {
  // Derive the effective kind from the displayed entities so that both
  // the column layout and the name column's defaultKind stay consistent
  // with the rows during filter transitions (when stale rows are kept
  // visible while new data loads).
  const effectiveKind =
    entities[0]?.kind?.toLocaleLowerCase('en-US') ?? filters.kind?.value;
  const showTypeColumn = filters.type === undefined;

  return [
    columnFactories.createTitleColumn({ hidden: true }),
    columnFactories.createNameColumn({ defaultKind: effectiveKind }),
    ...createEntitySpecificColumns(),
  ];

  function createEntitySpecificColumns(): TableColumn<CatalogTableRow>[] {
    const descriptionTagColumns = [
      columnFactories.createMetadataDescriptionColumn(),
      columnFactories.createTagsColumn(),
    ];
    const baseColumns = [
      columnFactories.createSystemColumn(),
      columnFactories.createOwnerColumn(),
      columnFactories.createSpecTypeColumn({ hidden: !showTypeColumn }),
      columnFactories.createSpecLifecycleColumn(),
    ];
    switch (effectiveKind) {
      case 'user':
        return [...descriptionTagColumns];
      case 'domain':
      case 'system':
        return [columnFactories.createOwnerColumn(), ...descriptionTagColumns];
      case 'group':
      case 'template':
        return [
          columnFactories.createSpecTypeColumn({ hidden: !showTypeColumn }),
          ...descriptionTagColumns,
        ];
      case 'location':
        return [
          columnFactories.createSpecTypeColumn({ hidden: !showTypeColumn }),
          columnFactories.createSpecTargetsColumn(),
        ];
      default:
        return entities.every(entity => entity.metadata.namespace === 'default')
          ? [...baseColumns, ...descriptionTagColumns]
          : [
              ...baseColumns,
              columnFactories.createNamespaceColumn(),
              ...descriptionTagColumns,
            ];
    }
  }
};
